import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { provisionCompany, quoteProvision } from "@/services/superAdminService";

/**
 * Direct provisioning for an offline-billed company — seats, plan, billing
 * cycle, negotiated rate and renewal, applied immediately with no payment
 * collected.
 *
 * Extracted verbatim from CompanyDetails so more than one page can drive the
 * same dialog. It was ~250 lines of state, derivation and submit logic welded
 * into that page, with the trigger button in one place and the dialog 900 lines
 * further down; a second caller could only have copied it, which would fork a
 * money path in two files.
 *
 * Behaviour is deliberately UNCHANGED by the move — including the field names
 * on the returned object, so the dialog JSX could be transplanted without
 * rewriting every binding.
 *
 * @param company    the full getCompanyById payload (needs `subscription`,
 *                   `latest_subscription`, `available_plans`, `company.state`
 *                   and `statistics.employee_count`)
 * @param companyId  id to provision against
 * @param onDone     called after a successful apply — refetch the page
 */
export function useProvisionCompany({ company, companyId, onDone }) {
  // Falls back to the latest subscription rather than only the live one: an
  // ENDED term is exactly when a super-admin adjusts seats, plan or rate before
  // marking the company paid, and applySeatsAndPlan supports that on purpose.
  // Binding this to the active-only record made the card disappear the moment a
  // company's term ended.
  const provisionTarget =
    company?.subscription || company?.latest_subscription || null;
  const isPaidOnline = !!provisionTarget?.is_paid_online;
  const canProvision = !!provisionTarget && !isPaidOnline;

  // Defensive: if the backend hasn't sent `available_plans` (older build), fall
  // back to at least the company's current plan so the dropdown still shows
  // what they are on rather than rendering blank.
  const availablePlans = useMemo(() => {
    const list = Array.isArray(company?.available_plans) ? company.available_plans : [];
    const currentId = company?.subscription?.plan_id;
    if (list.length) return list;
    return currentId
      ? [{
          plan_id: currentId,
          plan_name: company?.subscription?.plan_name || currentId,
          price_per_user_monthly: company?.subscription?.price_per_user_monthly,
          price_per_user_annual: company?.subscription?.price_per_user_annual,
          employee_limit: company?.subscription?.employee_limit ?? null,
          is_current: true,
        }]
      : [];
  }, [company]);

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionSeats, setProvisionSeats] = useState(1);
  const [provisionPlanId, setProvisionPlanId] = useState("");
  const [provisionCycle, setProvisionCycle] = useState("monthly");
  const [provisionCustom, setProvisionCustom] = useState(false);
  const [provisionPrice, setProvisionPrice] = useState("");
  const [provisionNote, setProvisionNote] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  // Renewal mode folds the old "Mark as Paid" step in: it adds a start-date
  // field, lets a term be renewed with no other change, and re-labels the CTA.
  const [provisionRenew, setProvisionRenew] = useState(false);
  const [provisionStartDate, setProvisionStartDate] = useState("");
  /**
   * What the server says this change costs.
   *
   * NOT derived here. The dialog used to compute its own headline from the
   * subscription's whole-cycle amounts, which was correct until mid-term
   * changes began billing only the months they actually buy — after that it
   * promised roughly 4x what it charged, and the gap was invisible on a
   * company whose term had just started. Recomputing proration client-side
   * would just be a second copy of the billing rules, free to drift from the
   * ones that raise the invoice, so the figure is asked for instead.
   */
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const openProvision = ({ renew = false } = {}) => {
    const sub = provisionTarget || {};
    const cycle = sub.billing_cycle || "monthly";
    setProvisionSeats(Number(sub.number_of_employees) || 1);
    setProvisionPlanId(sub.plan_id || "");
    setProvisionCycle(cycle);
    // Pre-tick only when the company is already on a negotiated rate, so opening
    // the dialog and saving never silently drops their custom pricing.
    setProvisionCustom(!!(sub.is_custom || sub.is_custom_course));
    setProvisionPrice(
      String((cycle === "annual" ? sub.price_per_user_annual : sub.price_per_user_monthly) ?? "")
    );
    setProvisionNote("");
    // A term whose end date is in the past is renewed regardless of how the
    // dialog was opened — you can't tweak seats on a dead subscription without
    // starting a fresh term.
    const ended = sub.end_date ? new Date(sub.end_date) < new Date() : false;
    setProvisionRenew(renew || ended);
    const d = new Date();
    setProvisionStartDate(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    );
    setProvisionOpen(true);
  };

  // Keep the price box showing the right rate when the cycle is toggled — an
  // annual rate is not a monthly one.
  useEffect(() => {
    if (!provisionOpen || provisionCustom) return;
    const plan = availablePlans.find((p) => p.plan_id === provisionPlanId);
    const source = plan || company?.subscription;
    if (!source) return;
    setProvisionPrice(
      String((provisionCycle === "annual"
        ? source.price_per_user_annual
        : source.price_per_user_monthly) ?? "")
    );
  }, [provisionCycle, provisionPlanId, provisionOpen, provisionCustom, availablePlans, company]);

  /**
   * Everything the dialog needs to describe the change, computed once.
   * Previously three separate inline IIFEs recomputed overlapping slices of
   * this, which is how the cap check and the submit button ended up disagreeing.
   */
  const provisionPreview = useMemo(() => {
    const sub = company?.subscription || company?.latest_subscription || {};
    // Every field is editable at once now, so the change kind is DERIVED from
    // what actually differs vs the current subscription rather than a mode the
    // trigger button picked.
    const switchingPlan = provisionPlanId !== sub.plan_id;
    const isPlanChange = switchingPlan;
    const cycle = provisionCycle;
    const rateOf = (src) =>
      src ? (cycle === "annual" ? src.price_per_user_annual : src.price_per_user_monthly) : null;

    const plan = availablePlans.find((p) => p.plan_id === provisionPlanId);
    const listRate = rateOf(plan);
    const currentRate = rateOf(sub);

    // Plan tier rank (display_order). A running, unexpired plan can only move UP
    // mid-term — a lower tier is a downgrade, which belongs at renewal. The
    // backend enforces this too; the dialog just blocks it before the round trip.
    const currentPlanOrder =
      (availablePlans.find((p) => p.plan_id === sub.plan_id) ||
        availablePlans.find((p) => p.is_current))?.display_order ?? null;
    const isDowngrade =
      switchingPlan &&
      plan?.display_order != null &&
      currentPlanOrder != null &&
      Number(plan.display_order) < Number(currentPlanOrder);

    const typed = Number(provisionPrice);
    const usingCustom = provisionCustom && provisionPrice !== "" && Number.isFinite(typed);
    // Not changing plan → keep whatever rate the company is on today; changing
    // plan → the new plan's list rate; a typed custom rate overrides either.
    const perUser = usingCustom ? typed : (isPlanChange ? listRate : currentRate);

    const seats = Number(provisionSeats);
    const validSeats = Number.isFinite(seats) && seats >= 1;
    const newAmount = validSeats && perUser != null ? +(perUser * seats).toFixed(2) : null;

    /**
     * What the company is on today — DERIVED, not read from `sub.amount`.
     *
     * `amount` is a denormalised field kept in step by a pre-save hook on the
     * model, which only fires on `.save()`. Any `updateOne` path leaves it
     * stale, and it has drifted badly before (the hook's own note records five
     * of seven dev subscriptions being wrong, one by ₹17,793). Recomputing it
     * from the rate and seats the subscription actually carries means the
     * before/after comparison can't quietly compare against a stale figure.
     */
    const currentSeatCount = Number(sub.number_of_employees ?? 0);
    const oldAmount =
      currentRate != null && Number.isFinite(currentSeatCount) && currentSeatCount > 0
        ? +(currentRate * currentSeatCount).toFixed(2)
        : null;

    /**
     * GST sits on top of the per-user rate and the company is invoiced the
     * GROSS figure — the dialog was quoting the base as if it were the total.
     * Maharashtra splits CGST+SGST 9/9, everywhere else is a single IGST 18.
     * Mirrors calculateSubscriptionGST on the server, which is what the
     * gateway would actually charge.
     */
    const GST_RATE = 0.18;
    const isMaharashtra =
      String(company?.company?.state || "").trim().toLowerCase() === "maharashtra";
    /**
     * Split the way calculateGST does — each half rounded from the BASE at 9%,
     * not the combined 18% halved.
     *
     * The dialog used to show `taxTotal / 2` as "each", which is a different
     * number: on a base of ₹91,319 the server bills 8218.71 + 8218.71 =
     * ₹16,437.42, while halving the 18% total shows ₹8,218.70 each and implies
     * ₹16,437.40. A paisa, but it is a paisa the invoice does not agree with,
     * and the two halves have to sum to the line above them.
     */
    const cgst = isMaharashtra && newAmount != null ? +(newAmount * 0.09).toFixed(2) : 0;
    const sgst = cgst;
    const igst = !isMaharashtra && newAmount != null ? +(newAmount * GST_RATE).toFixed(2) : 0;
    const taxTotal = newAmount != null ? +(cgst + sgst + igst).toFixed(2) : null;
    const grossAmount = newAmount != null ? +(newAmount + taxTotal).toFixed(2) : null;
    const oldGrossAmount =
      oldAmount != null ? +(oldAmount * (1 + GST_RATE)).toFixed(2) : null;

    /**
     * The change, on the SAME basis as the headline figure beside it.
     *
     * This used to be the ex-GST difference sitting next to a GST-inclusive
     * headline, so the two read as one sentence and disagreed: dropping ₹4,000
     * → ₹2,990 on 7 seats showed "₹24,697.40 / yr" and "−₹7,070", implying a
     * previous invoice of ₹31,767 that never existed. The real change was
     * −₹8,342.60 (₹33,040 → ₹24,697.40). One basis per tile.
     */
    const delta =
      grossAmount != null && oldGrossAmount != null
        ? +(grossAmount - oldGrossAmount).toFixed(2)
        : null;

    const cap = plan ? plan.employee_limit : sub.employee_limit;
    const overCap = cap != null && cap !== -1 && validSeats && seats > cap;

    /**
     * Seats bought during a running term cannot be handed back — the company
     * has been billed for them. Once the subscription is cancelled or expired
     * the constraint lifts, because renewal is a fresh commitment. The
     * employee-count floor always applies. Mirrors applySeatsAndPlan.
     */
    const employeeCount = Number(company?.statistics?.employee_count ?? 0);
    const currentSeats = Number(sub.number_of_employees ?? 0);
    const canReduceSeats = sub.can_reduce_seats === true;
    const seatFloor = canReduceSeats
      ? employeeCount
      : Math.max(employeeCount, currentSeats);
    const belowFloor = validSeats && seats < seatFloor;
    const floorReason =
      !canReduceSeats && currentSeats > employeeCount ? "licensed" : "headcount";

    const hadCustom = !!(sub.is_custom || sub.is_custom_course);
    const customWillBeLost =
      hadCustom && !provisionCustom && switchingPlan && listRate !== currentRate;
    const cycleChanging = cycle !== (sub.billing_cycle || "monthly");
    const seatsChanging = validSeats && seats !== Number(sub.number_of_employees ?? 0);
    /**
     * Taking a company OFF a negotiated rate. Unticking the box used to do
     * nothing at all unless the plan was also changing, so a company could be
     * put on a custom rate but never taken back off one.
     */
    const revertingToList = hadCustom && !provisionCustom;
    const planListRate = plan
      ? (cycle === "annual" ? plan.price_per_user_annual : plan.price_per_user_monthly)
      : null;
    /**
     * Nothing to submit. `usingCustom` alone used to count as a change, so
     * re-confirming the rate a company is already on wrote an audit row for a
     * change that never happened — which is the whole of a price-only request.
     * Compare the rate instead of the checkbox.
     */
    const rateChanging =
      (usingCustom && currentRate != null && Number(perUser) !== Number(currentRate)) ||
      revertingToList;
    // Price-only: the rate is the single thing that differs — plan, seats and
    // cycle all match what the company already has.
    const isPriceOnly =
      !switchingPlan && !seatsChanging && !cycleChanging && rateChanging;
    const nothingChanges =
      !switchingPlan && !cycleChanging && !seatsChanging && !rateChanging;

    return {
      sub, isPlanChange, isPriceOnly, rateChanging, revertingToList, planListRate,
      switchingPlan, seatsChanging,
      cycle, plan, listRate, currentRate, perUser, usingCustom,
      seats, validSeats, newAmount, oldAmount, oldGrossAmount, delta, cap, overCap,
      hadCustom, customWillBeLost, cycleChanging, nothingChanges,
      isMaharashtra, taxTotal, cgst, sgst, igst, grossAmount, isDowngrade, currentPlanOrder,
      employeeCount, currentSeats, seatFloor, belowFloor, floorReason, canReduceSeats,
    };
  }, [company, provisionCycle, provisionPlanId, provisionPrice,
      provisionCustom, provisionSeats, availablePlans]);

  /**
   * The request body for this change — used BOTH to price it and to apply it.
   *
   * One builder on purpose. A preview that assembled a slightly different body
   * than the submit would price something other than what gets applied, which
   * is the exact failure this quote exists to remove.
   */
  const buildProvisionPayload = () => {
    const p = provisionPreview;

    // Derive `type` from the diff. The server uses it only to decide which
    // fields it honours and how to label the audit row: plan_upgrade applies
    // the plan; custom_price pins seats/plan/cycle to current; seats applies
    // seats + cycle. A negotiated rate rides along with any of them.
    const type = p.isPlanChange
      ? "plan_upgrade"
      : p.isPriceOnly
        ? "custom_price"
        : "seats";

    const payload = {
      type,
      note: provisionNote.trim() || undefined,
    };
    // custom_price pins seats/plan/cycle to current on the server, so it
    // carries only the rate.
    if (type !== "custom_price") {
      payload.seats = Number(provisionSeats);
    }
    if (type === "plan_upgrade") {
      payload.plan_id = provisionPlanId;
      payload.billing_cycle = provisionCycle;
    } else if (type === "seats" && p.cycleChanging) {
      // A seats change can also move the billing cycle.
      payload.billing_cycle = provisionCycle;
    }
    if (provisionCustom && provisionPrice !== "") {
      payload.custom_price_per_user = Number(provisionPrice);
    } else if (p.revertingToList) {
      // Explicit: go back to the plan's published rate. Sending nothing would
      // have silently left the negotiated rate in place.
      payload.reset_to_list_price = true;
    }
    // Renewal: start a fresh paid term on the chosen date (the old "Mark as
    // Paid" step). Sent for EVERY type.
    //
    // This used to be withheld when `type` came out as "custom_price", to
    // mirror a server that pinned the date to null there. A renegotiated rate
    // is an ordinary thing to be the ONLY edit at renewal, though, so the two
    // halves agreed on dropping the renewal entirely: the dialog said "Renew
    // subscription" and "Starts a fresh paid term", the request succeeded, and
    // all that happened was the new rate landing on a term that had already
    // ended. The server pin is gone too — see provisionOfflineCompany.
    if (provisionRenew) {
      payload.renewal_start_date = provisionStartDate;
    }

    return payload;
  };

  /**
   * Ask the server what this change costs, debounced.
   *
   * Null (and so no request) whenever the change is not one the server would
   * accept anyway — dialog closed, nothing changed, a missing renewal date, or
   * a seat count the dialog already knows is out of bounds. Those are the same
   * conditions that disable the submit button, so a quote is never requested
   * for something that cannot be submitted.
   */
  const quotePayload = useMemo(() => {
    const p = provisionPreview;
    if (!provisionOpen || !canProvision) return null;
    if (!p.validSeats || p.overCap || p.belowFloor) return null;
    if (p.nothingChanges && !provisionRenew) return null;
    if (provisionRenew && !provisionStartDate) return null;
    if (p.isDowngrade && !provisionRenew) return null;
    return buildProvisionPayload();
    // eslint-disable-next-line
  }, [
    provisionOpen, canProvision, provisionPreview, provisionRenew, provisionStartDate,
    provisionSeats, provisionPlanId, provisionCycle, provisionCustom, provisionPrice,
  ]);

  const quoteKey = quotePayload ? JSON.stringify(quotePayload) : null;

  useEffect(() => {
    if (!quoteKey) {
      setQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return undefined;
    }

    let cancelled = false;
    setQuoteLoading(true);
    // Debounced: the seat stepper and the price box both fire per keystroke.
    const timer = setTimeout(async () => {
      try {
        const q = await quoteProvision(companyId, JSON.parse(quoteKey));
        if (cancelled) return;
        setQuote(q);
        setQuoteError(null);
      } catch (err) {
        if (cancelled) return;
        setQuote(null);
        // The server refuses what it would refuse on apply (plan cap, seat
        // floor, mid-term downgrade). Surface it — it is the reason the change
        // cannot be made, and the dialog should say so before it is attempted.
        setQuoteError(err?.response?.data?.error || "Could not price this change.");
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [quoteKey, companyId]);

  const submitProvision = async () => {
    const p = provisionPreview;
    // Guard here too, not just via the disabled button — re-confirming the same
    // values must never write an audit row for a change that never happened.
    // A renewal is exempt: starting a fresh term IS the change, even when seats,
    // plan and rate all stay the same.
    if (p.nothingChanges && !provisionRenew) {
      toast.error("No changes to apply.");
      return;
    }
    if (provisionRenew && !provisionStartDate) {
      toast.error("Pick the date the new term starts.");
      return;
    }
    // A live, unexpired plan can only move up. Downgrades belong at renewal.
    if (p.isDowngrade && !provisionRenew) {
      toast.error("This plan is on a running term — downgrade it at renewal, not mid-term.");
      return;
    }
    setProvisioning(true);
    try {
      const payload = buildProvisionPayload();

      const res = await provisionCompany(companyId, payload);
      toast.success(res?.message || "Company updated.");
      setProvisionOpen(false);
      await onDone?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not update this company. Please try again."
      );
    } finally {
      setProvisioning(false);
    }
  };

  return {
    // Whether a trigger should be offered at all.
    provisionTarget,
    isPaidOnline,
    canProvision,
    availablePlans,
    // Dialog state — same names the JSX already binds to.
    provisionOpen, setProvisionOpen,
    provisionSeats, setProvisionSeats,
    provisionPlanId, setProvisionPlanId,
    provisionCycle, setProvisionCycle,
    provisionCustom, setProvisionCustom,
    provisionPrice, setProvisionPrice,
    provisionNote, setProvisionNote,
    provisioning,
    provisionRenew,
    provisionStartDate, setProvisionStartDate,
    provisionPreview,
    // Server-priced. `quote.charge.gross_amount` is what the invoice will be
    // raised for — prefer it over anything derived from provisionPreview.
    quote,
    quoteLoading,
    quoteError,
    /**
     * Whether a price is coming for the current inputs.
     *
     * The dialog needs this to know the difference between "no charge applies
     * here" and "the charge has not arrived yet". Without it, the only way to
     * fill the gap is a locally-derived figure — which is a whole-cycle amount
     * and so flashes a price up to 12x too high before the real one lands.
     */
    quoteExpected: quotePayload != null,
    openProvision,
    submitProvision,
  };
}
